import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import { ArticleSearchHero } from '#/components/ArticleSearchHero/ArticleSearchHero';
import { Box } from '#/components/Box/Box';
import { ClientOnly } from '#/containers/ClientOnly';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useTrackScrollTrigger } from '#/hooks/useTrackScrollTrigger';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiPagePage } from '#/modules/cms/api/types/CmsApiPage';
import { CmsRecommendedJobsBlock } from '#/modules/cms/blocks/RecommendedJobs/CmsRecommendedJobsBlock';
import {
  CmsBodyHolder,
  CmsBodyHolderWidth,
} from '#/modules/cms/components/BodyHolder/CmsBodyHolder';
import { CmsMobileTableOfContents } from '#/modules/cms/components/TableOfContents/CmsMobileTableOfContents';
import { getCmsApiPageDefaultSearchOrPostSearchType } from '#/modules/cms/helpers/getCmsApiPageDefaultSearchOrPostSearchType';
import { SearchHero } from '#/modules/search/components/Hero/SearchHero';
import { LazyFixedStaffTools } from '#/modules/staff/components/FixedStaffTools/LazyFixedStaffTools';
import { userDashboardApiEnableProfileFeatures } from '#/modules/userDashboard/api/userDashboardApiEnableProfileFeatures';
import { useCurrentUserIsStaff } from '#/store/hooks/useCurrentUserIsStaff';
import { sectionPadding } from '#/theme/theme';
import { showModal } from '#/zustand-stores/modalStore';

import { CmsHelpPageFeedback } from './CmsHelpPageFeedback';

type Props = { content: CmsApiPagePage; children: ReactNode };

export function CmsPagePageLayout({ content, children }: Props) {
  const { i18n } = useI18n();

  const isStaff = useCurrentUserIsStaff();
  const { user } = useUser();

  useTrackScrollTrigger('cms-content-end', 'End of Content');

  const hasBelowBlocks = useMemo(() => {
    if (content.hasRecommendedJobs) return true;
    return content.hasReactions || content.enableComments;
  }, [
    content.enableComments,
    content.hasReactions,
    content.hasRecommendedJobs,
  ]);

  useEffect(() => {
    if (
      content.enableProfileOnVisit &&
      user?.emailVerified &&
      !user.hasIdealistProfileFeature
    ) {
      void userDashboardApiEnableProfileFeatures().then(() => {
        showModal('CREATE_USER_PROFILE', {
          title: i18n('Create Your Profile'),
          description: i18n(
            'To continue, please review the fields below and create a public profile.',
          ),
        });

        return undefined;
      });
    }
  }, [
    i18n,
    content.enableProfileOnVisit,
    user?.emailVerified,
    user?.hasIdealistProfileFeature,
  ]);

  const hero = content.heroSearch?.onlyResults ? undefined : content.heroSearch;
  const heroDefaultSearchOrPostSearchType =
    getCmsApiPageDefaultSearchOrPostSearchType({ content });

  const bottomTableOfContents = content.tableOfContents;

  return (
    <>
      {isStaff && <LazyFixedStaffTools content={content} />}

      {bottomTableOfContents && bottomTableOfContents.length > 0 && (
        <ClientOnly>
          <CmsMobileTableOfContents
            parents={bottomTableOfContents}
            hasButtonBar={false}
          />
        </ClientOnly>
      )}

      {hero && (
        <div style={{ position: 'relative' }}>
          {heroDefaultSearchOrPostSearchType === 'CMS_PAGE' ? (
            <ArticleSearchHero
              title={hero.text}
              image={hero.image}
              imageMobile={hero.imageMobile}
              content={content}
            />
          ) : (
            <SearchHero
              title={hero.text}
              image={hero.image ?? undefined}
              imageMobile={hero.imageMobile ?? undefined}
              defaultSearchType={heroDefaultSearchOrPostSearchType}
            />
          )}
        </div>
      )}

      {children}

      {hasBelowBlocks && (
        <Box flex="1 1 auto" width="100%" m={sectionPadding} mt={70}>
          {content.hasRecommendedJobs && (
            <CmsBodyHolder $width={CmsBodyHolderWidth.NARROW_LAYOUT_WIDE}>
              <CmsRecommendedJobsBlock
                data={{
                  headline: content.recommendedJobsHeadline ?? null,
                  layout: 'REGULAR',
                  jobFunction: null,
                  areaOfFocus: null,
                }}
              />
            </CmsBodyHolder>
          )}
        </Box>
      )}

      <CmsHelpPageFeedback
        contentId={content.id}
        contentTitle={content.title}
      />
    </>
  );
}
